import type { ServiceDto, SiteSettingsDto } from '@portfolio/shared';
import { Highlight, plainText } from '../Highlight';
import { Reveal } from '../Reveal';
import { SmartLink } from '../SmartLink';

export function Services({ s, services }: { s: SiteSettingsDto; services: ServiceDto[] }) {
  if (!services.length) return null;
  return (
    <section
      id="services"
      aria-label={plainText(s.servicesHeading)}
      className="container-site pb-20 md:pb-[163px]"
    >
      <div aria-hidden className="h-px bg-line" />
      <Reveal className="grid gap-8 pt-16 md:grid-cols-[minmax(0,1fr)_minmax(0,302px)] md:items-end md:gap-x-9 md:pt-[94px]">
        <h2 className="display-xl max-w-[10ch] text-strong md:max-w-none">
          <Highlight text={s.servicesHeading} />
        </h2>
        {s.servicesIntro && (
          <p className="text-body text-graphite md:-mb-[3px] md:text-justify">{s.servicesIntro}</p>
        )}
      </Reveal>

      <ul className="mt-10 border-t border-line md:mt-[62px]">
        {services.map((svc, i) => (
          <Reveal
            as="li"
            key={svc.id}
            delay={Math.min(i * 0.05, 0.2)}
            className="group grid gap-x-10 gap-y-4 border-b border-line py-8 last:border-b-0 md:min-h-[205px] md:grid-cols-[minmax(0,1fr)_minmax(0,384px)] md:pt-[57px] md:pb-10"
          >
            <h3 className="flex items-baseline text-[clamp(1.75rem,1rem+2.6vw,3.125rem)] leading-none tracking-normal">
              <span className="font-extralight text-silver tabular-nums transition-colors duration-300 group-hover:text-primary">
                {svc.icon ?? String(i + 1).padStart(2, '0')}
              </span>
              <span aria-hidden className="mx-[0.16em] font-extralight text-silver">
                |
              </span>
              <span className="font-bold text-strong transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:translate-x-1.5 rtl:group-hover:-translate-x-1.5">
                {svc.title}
              </span>
            </h3>
            {svc.description && (
              <p className="text-body text-graphite md:pt-[17px] md:text-justify">
                {svc.description}
              </p>
            )}
          </Reveal>
        ))}
      </ul>

      <SmartLink
        href="#contact"
        className="mt-2 inline-flex h-12 items-center rounded-lg bg-ink px-6 text-ui font-semibold md:mt-0 text-canvas transition-colors hover:bg-primary"
      >
        {s.contactCtaLabel}
      </SmartLink>
    </section>
  );
}
